// Evidencia multimedia (fotos / videos) de los tickets del Portal de Tickets de
// Seguimiento. Espejo del patrón de adjuntos del CRM (crm-adjuntos.tsx): reusa el
// bucket público `documentos` con prefijo `tickets/<id_ticket>/...` y registra
// cada archivo en la tabla `tickets_adjuntos`.
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { safeExtension } from '@/lib/storage-keys';

// La tabla tickets_adjuntos no está en los tipos generados de Supabase → cast puntual.
const sb = supabase as any;

export const TICKETS_ATTACH_BUCKET = "documentos";

// Límites de negocio (se validan en el front; el bucket `documentos` no tiene límite).
export const MAX_FOTO_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50 MB
export const MAX_AUDIO_BYTES = 25 * 1024 * 1024; // 25 MB
export const MAX_DOC_BYTES = 25 * 1024 * 1024; // 25 MB (documentos: PDF/Word/Excel/ZIP…)
export const MAX_ADJUNTOS = 10; // por ticket, por campo (evidencia / documentos)

export type AdjuntoTipo = "foto" | "video" | "audio" | "documento";

// Grupos de tipos por "campo" del formulario (evidencia multimedia vs documentos).
export const MEDIA_TIPOS: AdjuntoTipo[] = ["foto", "video", "audio"];
export const DOC_TIPOS: AdjuntoTipo[] = ["documento"];
// `accept` de los <input type=file> por campo.
export const ACCEPT_MEDIA = "image/*,video/*,audio/*";
export const ACCEPT_DOCS = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp,.md,.markdown,.zip";
// Campo unificado: multimedia + documentos en el mismo dropzone.
export const ACCEPT_ALL = `${ACCEPT_MEDIA},${ACCEPT_DOCS}`;

// Extensiones que consideramos "documento" (clasificación confiable por extensión).
const DOC_EXTS = new Set([
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "rtf", "odt", "ods", "odp",
  "md", "markdown",
  // Comprimidos: varios archivos (logs, exportes, capturas) en un solo adjunto.
  "zip",
]);

// Archivo elegido pero aún NO subido (usado al CREAR el ticket, que todavía no tiene id).
export type PendingAdjunto = {
  id: string;
  file: File;
  tipo: AdjuntoTipo;
  nombre: string;
  previewUrl: string;
};

// Archivo ya subido y registrado.
export type TicketAdjunto = {
  id: number;
  url: string;
  tipo: AdjuntoTipo;
  nombre: string;
  mime: string | null;
  tamano: number | null;
  /** Para ubicar el archivo en la línea de tiempo del ticket. */
  fecha?: string | null;
  autorId?: string | null;
  /** true = el cliente/agente lo ve en su app (lo subió él o se lo mandamos en la conversación). */
  visibleCliente?: boolean;
};

export function humanFileSize(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Clasifica un File como foto/video/audio/documento; null si no es ninguno (no permitido).
export function classifyTicketFile(file: File): AdjuntoTipo | null {
  if (file.type.startsWith("image/")) return "foto";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const t = file.type.toLowerCase();
  const esDoc =
    DOC_EXTS.has(ext) ||
    t.includes("pdf") ||
    t.includes("word") ||
    t.includes("excel") ||
    t.includes("spreadsheet") ||
    t.includes("presentation") ||
    t.includes("powerpoint") ||
    t.includes("officedocument") ||
    t.includes("opendocument") ||
    t === "text/plain" ||
    t === "text/csv" ||
    t === "text/markdown" ||
    t === "application/rtf" ||
    // Windows reporta el .zip como `application/x-zip-compressed`.
    t === "application/zip" ||
    t === "application/x-zip-compressed";
  if (esDoc) return "documento";
  return null;
}

// Valida tipo y tamaño. `permitidos` restringe a los tipos de un campo (evidencia / documentos).
// Devuelve el mensaje de error, o null si es válido.
export function validateTicketFile(file: File, permitidos?: AdjuntoTipo[]): string | null {
  const tipo = classifyTicketFile(file);
  if (!tipo) return `"${file.name}" no es un tipo de archivo permitido.`;
  if (permitidos && !permitidos.includes(tipo)) {
    return permitidos.length === 1 && permitidos[0] === "documento"
      ? `"${file.name}" no es un documento (PDF, Word, Excel, ZIP…).`
      : `"${file.name}" no es una foto, video ni audio.`;
  }
  const max =
    tipo === "foto" ? MAX_FOTO_BYTES
    : tipo === "video" ? MAX_VIDEO_BYTES
    : tipo === "audio" ? MAX_AUDIO_BYTES
    : MAX_DOC_BYTES;
  if (file.size > max) {
    const lim = tipo === "foto" ? "10 MB" : tipo === "video" ? "50 MB" : "25 MB";
    const cual =
      tipo === "foto" ? "fotos" : tipo === "video" ? "videos" : tipo === "audio" ? "audios" : "documentos";
    return `"${file.name}" pesa ${humanFileSize(file.size)}; el máximo para ${cual} es ${lim}.`;
  }
  return null;
}

// Convierte un File en PendingAdjunto (con preview local) o null si es inválido (avisa con toast).
export function toPendingAdjunto(file: File, permitidos?: AdjuntoTipo[]): PendingAdjunto | null {
  const err = validateTicketFile(file, permitidos);
  if (err) {
    toast.error(err);
    return null;
  }
  const tipo = classifyTicketFile(file)!;
  return { id: crypto.randomUUID(), file, tipo, nombre: file.name, previewUrl: URL.createObjectURL(file) };
}

// Imágenes contenidas en un evento de pegado (para pegar capturas directo en la descripción).
export function imagenesPegadas(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const deFiles = Array.from(dt.files ?? []).filter((f) => f.type.startsWith("image/"));
  if (deFiles.length) return deFiles;
  // Algunos navegadores ponen la captura en `items` en vez de `files`.
  const out: File[] = [];
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind === "file" && it.type.startsWith("image/")) {
      const f = it.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}

// Pegado desde el portapapeles: vive en un módulo sin Supabase para poder probarlo.
export { archivosPegados, leerArchivosPortapapeles, conNombreImagen, pegadoTraeTexto } from "./pegado";

// Content-Type para la subida. En archivos de texto (md/txt/csv) forzamos `charset=utf-8`:
// sin él, al abrirlos el navegador usa una codificación heredada y el UTF-8 se ve con
// "símbolos raros" (mojibake: ó→Ã³, -→Â-, etc.). Los binarios (PDF/Word/…) quedan igual.
function uploadContentType(file: File): string {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const TEXTO_UTF8: Record<string, string> = {
    md: "text/markdown; charset=utf-8",
    markdown: "text/markdown; charset=utf-8",
    txt: "text/plain; charset=utf-8",
    csv: "text/csv; charset=utf-8",
  };
  if (TEXTO_UTF8[ext]) return TEXTO_UTF8[ext];
  const t = file.type || "";
  if (t.startsWith("text/") && !/charset=/i.test(t)) return `${t}; charset=utf-8`;
  return t || "application/octet-stream";
}

// Sube un archivo al bucket y devuelve sus metadatos, o null (con toast) si falla.
export async function uploadTicketFile(
  idTicket: number | string,
  file: File,
): Promise<{ url: string; tipo: AdjuntoTipo; nombre: string; mime: string | null; tamano: number } | null> {
  const tipo = classifyTicketFile(file);
  if (!tipo) return null;
  const safeExt = safeExtension(file.name);
  const path = `tickets/${idTicket}/${crypto.randomUUID()}.${safeExt}`;
  const { data, error } = await sb.storage
    .from(TICKETS_ATTACH_BUCKET)
    .upload(path, file, { contentType: uploadContentType(file), upsert: false });
  if (error || !data) {
    toast.error(`No se pudo subir "${file.name}": ${error?.message ?? "error desconocido"}`);
    return null;
  }
  const { data: pub } = sb.storage.from(TICKETS_ATTACH_BUCKET).getPublicUrl(data.path);
  return { url: pub.publicUrl, tipo, nombre: file.name, mime: file.type || null, tamano: file.size };
}

// Sube y registra una lista de adjuntos pendientes para un ticket ya creado.
// Best-effort: si la tabla no existe aún (migración no aplicada), avisa pero no rompe.
export async function saveTicketAdjuntos(
  idTicket: number | string,
  userId: string | undefined | null,
  pendientes: PendingAdjunto[],
  opts?: { visibleCliente?: boolean },
): Promise<void> {
  for (const a of pendientes) {
    const up = await uploadTicketFile(idTicket, a.file);
    if (!up) continue; // uploadTicketFile ya mostró el motivo exacto
    const { error } = await sb.from("tickets_adjuntos").insert({
      id_ticket: Number(idTicket),
      tipo: up.tipo,
      url: up.url,
      nombre: up.nombre,
      mime: up.mime,
      tamano_bytes: up.tamano,
      id_usuario_autor: userId ?? null,
      // Default de la tabla = false (evidencia interna). Solo la conversación con el cliente
      // lo manda en true, que es lo que leen las apps (cliente-tickets / agente-tickets).
      visible_cliente: opts?.visibleCliente ?? false,
    });
    if (error) {
      console.warn("tickets_adjuntos insert:", error.message);
      toast.error(`Se subió "${up.nombre}" pero no se registró: ${error.message}`);
    }
  }
}

// Carga los adjuntos activos de un ticket. Best-effort: si la tabla no existe, devuelve [].
export async function fetchTicketAdjuntos(idTicket: number | string): Promise<TicketAdjunto[]> {
  const res = await sb
    .from("tickets_adjuntos")
    .select("id, url, tipo, nombre, mime, tamano_bytes, fecha_creacion, id_usuario_autor, visible_cliente")
    .eq("id_ticket", Number(idTicket))
    .eq("activo", true)
    .order("id", { ascending: true });
  if (res.error) return [];
  return (res.data ?? []).map((r: any) => ({
    id: r.id,
    url: r.url,
    tipo: r.tipo,
    nombre: r.nombre,
    mime: r.mime ?? null,
    tamano: r.tamano_bytes ?? null,
    fecha: r.fecha_creacion ?? null,
    autorId: r.id_usuario_autor ?? null,
    visibleCliente: r.visible_cliente === true,
  }));
}

// Soft-delete de un adjunto (la RLS solo lo permite a Super Admin, rol 1). Best-effort
// intenta también quitar el archivo físico del bucket.
export async function deleteTicketAdjunto(adj: TicketAdjunto): Promise<boolean> {
  const { error } = await sb.from("tickets_adjuntos").update({ activo: false }).eq("id", adj.id);
  if (error) {
    toast.error(`No se pudo borrar la evidencia: ${error.message}`);
    return false;
  }
  try {
    const marker = `/object/public/${TICKETS_ATTACH_BUCKET}/`;
    const idx = adj.url.indexOf(marker);
    if (idx >= 0) {
      const path = decodeURIComponent(adj.url.slice(idx + marker.length));
      await sb.storage.from(TICKETS_ATTACH_BUCKET).remove([path]);
    }
  } catch {
    /* si no hay permiso de storage, el registro ya quedó oculto (activo=false) */
  }
  return true;
}

// Cambia si el cliente ve el archivo en su app. La RLS solo permite UPDATE al Super Admin
// (rol 1) y PostgREST no devuelve error cuando la política deja la fila fuera: se pide la
// fila de vuelta con `select` para distinguir "no se pudo" de "no se cambió nada".
export async function setTicketAdjuntoVisibleCliente(
  adj: TicketAdjunto,
  visible: boolean,
): Promise<boolean> {
  const { data, error } = await sb
    .from("tickets_adjuntos")
    .update({ visible_cliente: visible })
    .eq("id", adj.id)
    .select("id");
  if (error) {
    toast.error(`No se pudo cambiar la visibilidad: ${error.message}`);
    return false;
  }
  if (!data?.length) {
    toast.error("No se pudo cambiar la visibilidad: solo un Super Admin puede hacerlo.");
    return false;
  }
  return true;
}
